import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import { firstValueFrom, takeUntil } from 'rxjs';
import { NavController } from '@ionic/angular';
import { Subject } from 'rxjs/internal/Subject';
import { Order } from '../../dto/order.dto';
import { ImageService } from '../../services/image.service';
import { Status } from '../../dto/status.enum';
import { REQUESTS_IN_WORK, OPEN_REQUESTS, ARCHIVED_REQUESTS } from '../../utils/constants/router.constants';
import { MenuTabChangeService } from '../../services/menu-tab-change.service';
import { ErrorToastService } from '../../services/error-toast.service';
import { OrdersService } from '../../services/order/orders.service';
import { OrderChangesService } from '../../services/order/order-changes.service';
import { RtsDay } from '../../dto/get-rts-result.dto';
import { HeaderDto } from '../../dto/header.dto';
import { AcceptOrderModalService } from '../../services/modal/accept-order-modal.service';
import * as Sentry from '@sentry/angular';

@Component({
  selector: 'app-open-request-info',
  templateUrl: './open-request-info.html',
  styleUrls: ['./open-request-info.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
/**
 * Экран открытой (ещё не принятой) заявки: реквизиты, дни РТС, кнопка «Принять». Данные — из реактивного
 * потока заказа (нет состояния — грузим с сервера, есть — берём из потока). Экран обслуживает маршруты
 * разных вкладок (открытые / в работе / архив), поэтому проверяет соответствие статуса заявки текущей
 * вкладке ({@link #navigateToTabRootIfIncompatible}): при рассогласовании уводит в корень нужной вкладки —
 * например, после принятия заявка уходит из «открытых» в «в работе». Стратегия OnPush.
 */
export class OpenRequestInfoComponent implements OnInit, OnDestroy {
  orderID: number;
  request: Order;
  loading: boolean;
  isRefresherDisabled: boolean;
  targetUrl = window.location.pathname.split('/')[1];
  phoneHref: string;
  daysList: RtsDay[];
  headerDataValues: HeaderDto;
  defaultBackUrl: string = '/requests';

  private destroy$ = new Subject();
  private updateCount = 0;

  constructor(
    readonly imageService: ImageService,
    private navCtrl: NavController,
    private errorToastService: ErrorToastService,
    private ordersService: OrdersService,
    private orderChangesService: OrderChangesService,
    private cd: ChangeDetectorRef,
    private acceptOrderModalService: AcceptOrderModalService,
    private tabChangeService: MenuTabChangeService
  ) {}

  ngOnInit() {
    this.loading = true;

    this.orderID =
      +window.location.pathname.split('/')[
        window.location.pathname.split('/').findIndex(el => el === 'days') + 1
      ];

    this.headerDataValues = {
      title: `Заявка №${this.orderID}`
    };

    this.isRefresherDisabled = true;
    this.orderChangesService
      .getOrderSubject(this.targetUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe(orderUpdate => {
        this.updateCount += 1;
        this.loading = true;
        // Поток ведёт экран: нет состояния — грузим с сервера, есть — берём из потока. Перед показом
        // проверяем, что статус заявки подходит текущей вкладке; иначе уводим в корень нужной вкладки.
        if (!orderUpdate) {
          this.ordersService.getOrderById(this.orderID).subscribe(order => {
            if (order?.success) {
              if (this.navigateToTabRootIfIncompatible(order.data)) {
                this.loading = false;
                this.isRefresherDisabled = false;
                return;
              }
              this.pageInitActions(order.data);
            } else {
              this.errorToastService.acceptOrderErrorModal().then();
              this.navCtrl.back();
            }
            this.loading = false;
            this.isRefresherDisabled = false;
          });
        } else {
          if (this.navigateToTabRootIfIncompatible(orderUpdate)) {
            this.loading = false;
            this.isRefresherDisabled = false;
            return;
          }
          this.pageInitActions(orderUpdate);
          this.loading = false;
          this.isRefresherDisabled = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  calculateTotalFee() {
    return this.request.requestedWeightInTons * this.request.forwarderFee;
  }

  async acceptOrder() {
    return this.acceptOrderModalService.acceptOrderModal(() =>
      this.performAcceptOrder()
    );
  }

  /**
   * Принятие заявки: вызываем {@code acceptOrder}, перечитываем заказ и кладём его в потоки вкладок
   * «открытые» и «в работе», просим обновить список открытых, сбрасываем состояние экрана и переходим к
   * заявке в работе. Если перечитать не удалось — уходим просто в список «в работе».
   */
  private async performAcceptOrder(): Promise<void> {
    try {
      await firstValueFrom(
        this.ordersService.acceptOrder(this.request.id)
      );
      const orderRes = await firstValueFrom(
        this.ordersService.getOrderById(this.request.id)
      );
      const createdRequest = orderRes?.success ? orderRes.data : null;
      if (createdRequest) {
        this.orderChangesService.setOrder(createdRequest, REQUESTS_IN_WORK);
        this.orderChangesService.setOrder(createdRequest, OPEN_REQUESTS);

        this.tabChangeService.refreshData.next(OPEN_REQUESTS);

        this.resetComponentState();

        await this.navCtrl.navigateRoot([`/${OPEN_REQUESTS}`], { animated: false });

        await this.navCtrl.navigateRoot([
          `/requests-in-work/days/${this.request.id}`
        ]);
      } else {
        await this.navCtrl.navigateRoot(['/requests-in-work']);
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  // Допустим ли статус заявки в текущей вкладке (открытые = OPEN; в работе = IN_WORK/ACCEPTED;
  // архив = COMPLETED/CLOSED).
  private isStatusAllowedInCurrentTab(status: Status): boolean {
    switch (this.targetUrl) {
      case OPEN_REQUESTS:
        return status === Status.OPEN;
      case REQUESTS_IN_WORK:
        return status === Status.IN_WORK || status === Status.ACCEPTED;
      case ARCHIVED_REQUESTS:
        return status === Status.COMPLETED || status === Status.CLOSED;
      default:
        return true;
    }
  }

  /**
   * Если статус заказа не соответствует текущей вкладке ({@link #isStatusAllowedInCurrentTab}) — уводит в
   * корень нужной вкладки и возвращает {@code true} (вызывающий прекращает отрисовку). Нужно, когда
   * заявка сменила статус (принята/завершена) и в этой вкладке ей уже не место.
   */
  private navigateToTabRootIfIncompatible(order: Order): boolean {
    if (!order) return false;
    if (this.isStatusAllowedInCurrentTab(order.status)) {
      return false;
    }
    let root = '/';
    switch (this.targetUrl) {
      case OPEN_REQUESTS:
        root = `/${OPEN_REQUESTS}`;
        break;
      case REQUESTS_IN_WORK:
        root = `/${REQUESTS_IN_WORK}`;
        break;
      case ARCHIVED_REQUESTS:
        root = `/${ARCHIVED_REQUESTS}`;
        break;
      default:
        root = '/';
    }
    this.navCtrl.navigateRoot([root]);
    return true;
  }

  private pageInitActions(r: Order) {
    this.request = r;
    this.daysList = this.request?.registry?.days;
    this.phoneHref = `tel:${this.request.seller.phone}`;
    this.cd.markForCheck();
  }

  // Сброс состояния экрана перед переходом (в т.ч. отписка через destroy$), чтобы не мигали старые данные.
  private resetComponentState(): void {
    this.destroy$.next(null);
    this.loading = true;
    this.isRefresherDisabled = true;
    this.daysList = [];
    this.phoneHref = '';
    this.cd.markForCheck();
  }
}
